import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Link as RouterLink } from '~/lib/react-router-dom';

import { Box, Button, Grid, Link, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import useQuery from '~/hooks/useQuery';

import { BidHistoryRecord, getAdGroupBidHistory } from '~/services/PublicOfferFeedService';

import dateFormatter from '~/utils/dateFormatter';

interface FormValues {
  customerId?: string;
  campaignId?: string;
  adGroupId?: string;
  search?: string;
}

interface QueryParams extends FormValues {
  page_num: number;
  page_size: number;
}

const columns: GridColDef[] = [
  {
    field: 'customerId',
    headerName: 'Account Id',
    flex: 1,
  },
  {
    field: 'campaignId',
    headerName: 'Campaign Id',
    flex: 1,
    renderCell: (params) => (
      <Link
        component={RouterLink}
        to={`/marketing/google-ads/customer/${params.row.customerId}/campaign/${params.row.campaignId}`}
        underline="hover"
      >
        {params.row.campaignId}
      </Link>
    ),
  },
  {
    field: 'adGroupId',
    headerName: 'Ad Group Id',
    flex: 1,
    renderCell: (params) => (
      <Link
        component={RouterLink}
        to={`/marketing/google-ads/customer/${params.row.customerId}/campaign/${params.row.campaignId}/ad-group/${params.row.adGroupId}`}
        underline="hover"
      >
        {params.row.adGroupId}
      </Link>
    ),
  },
  {
    field: 'difference',
    headerName: 'Target Difference %',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params) => {
      const targetDiff =
        params.row.differencePercentage ??
        ((params.row.target - params.row.previousTarget) * 100) / params.row.previousTarget;
      return (
        <Typography fontSize={16} color={targetDiff > 0 ? 'green' : 'red'}>
          {targetDiff > 0 ? '+' : ''}
          {Number(targetDiff).toFixed(2)}%
        </Typography>
      );
    },
  },
  {
    field: 'target',
    headerName: 'Target',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
  },
  {
    field: 'previousTarget',
    headerName: 'Previous Target',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
  },
  {
    field: 'biddingStrategy',
    headerName: 'bidding Strategy',
    align: 'right',
    headerAlign: 'right',
    flex: 1,
  },
  {
    field: 'changedBy',
    headerName: 'Changed By',
    flex: 2,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => params.row.changedBy ?? 'Daily Automation',
  },
  {
    field: 'updatedAt',
    headerName: 'Updated At',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => dateFormatter(params.row.updatedAt),
  },
];

function BidModificationHistory() {
  const query = useQuery();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<Array<BidHistoryRecord>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pageNum, setPageNum] = useState(Number(query.get('page_num') ?? 0));
  const [pageSize, setPageSize] = useState(Number(query.get('page_size') ?? 100));
  const [totalCount, setTotalCount] = useState(0);
  const history = useHistory();

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormValues>({
    defaultValues: {
      customerId: query.get('customerId') || '',
      campaignId: query.get('campaignId') || '',
      adGroupId: query.get('adGroupId') || '',
      search: query.get('search') || '',
    },
  });

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum,
      page_size: pageSize,
      customerId: getValues('customerId'),
      campaignId: getValues('campaignId'),
      adGroupId: getValues('adGroupId'),
      search: getValues('search'),
    } as QueryParams);

    history.push({
      search: `?${searchParams}`,
    });
  }, [getValues, history, pageNum, pageSize]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const { result, count } = await getAdGroupBidHistory({
        customerId: getValues('customerId'),
        campaignId: getValues('campaignId'),
        adGroupId: getValues('adGroupId'),
        search: getValues('search'),
        page: pageNum,
        limit: pageSize,
      });

      setData(result);
      setTotalCount(Number(count));
      setQueryParams();
    } catch (error) {
      enqueueSnackbar(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [pageNum, pageSize, getValues, setQueryParams, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = useCallback(async () => {
    fetchData();
  }, [fetchData]);

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      setPageNum(page);
      setPageSize(pageSize);
    },
    [fetchData, query],
  );

  useEffect(() => {
    fetchData();
  }, [pageNum, pageSize, fetchData]);

  return (
    <Box sx={{ m: 3 }}>
      <Helmet>
        <title>Bid Modification History</title>
      </Helmet>

      <PageSubheader title={`Search Bid Modification History`} />

      <Grid
        container
        spacing={2}
        justifyContent="flex-start"
        alignItems="center"
        component="form"
        onSubmit={handleSubmit(handleSearch)}
      >
        <Grid item>
          <TextField
            variant="outlined"
            label="Customer ID"
            {...register('customerId')}
            error={!!errors.customerId}
            helperText={errors.customerId?.message}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="outlined"
            label="Campaign ID"
            {...register('campaignId')}
            error={!!errors.campaignId}
            helperText={errors.campaignId?.message}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="outlined"
            label="Ad Group ID"
            {...register('adGroupId')}
            error={!!errors.adGroupId}
            helperText={errors.adGroupId?.message}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="outlined"
            label="Search"
            {...register('search')}
            error={!!errors.search}
            helperText={errors.search?.message}
            sx={{ width: 300 }}
          />
        </Grid>

        <Grid item>
          <Button variant="contained" color="primary" type="submit">
            Search
          </Button>
        </Grid>
      </Grid>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={data}
          columns={columns}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[100, 250, 500]}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
        />
      </Box>
    </Box>
  );
}

export default BidModificationHistory;
